import {
  Box,
  Text,
  VStack,
  Button,
  HStack,
  Divider,
  Icon,
  Stack,
} from "@chakra-ui/react";
import {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useCallback,
} from "react";
import { Select } from "chakra-react-select";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { FaInfo, FaCalendar, FaUsers } from "react-icons/fa";
import { useLoading } from "../../../../hooks/form";
import { useDialogWithOptions } from "../../../../../Common/Dialog";
import { StepProps } from "./types";
import ColumnMatchingModal, {
  ColumnMatchingModalContext,
  withColumnMatchingModal,
} from "./components/ColumnMatchingDialog";
import WhiteBgInput from "./WhiteBgInput";
import { yupResolver } from "@hookform/resolvers/yup";
import { AdSetFormData, AdSetFormSchema } from "./forms/adset";
import InfoSection from "../../../../../ui/InfoSection";
import { AudienceTypeSelector } from "./components/audience/AudienceSelector";
import KYDSwitch from "src/ui/KYDSwitch";
import CollapsibleContainer from "./components/CollapsibleContainer";
import BudgetSelector from "./components/BudgetSelector";
import adsetTransform from "./transform/adset";
import Container from "./components/Container";
import WarningBox from "./components/WarningBox";
import {
  CustomConversionsWarning,
  ManageCustomConversionsModal,
  type EnrichedCustomConversion,
} from "src/components/CustomConversions";
import { useAudiences } from "./hooks/useAudiences";
import { AudienceData, CsvAudienceData } from "@common/interfaces";
import dayjs from "dayjs";
import actions from "src/actions";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

export const InputLabel = ({ children }: { children: React.ReactNode }) => (
  <Text w="full" fontWeight={"medium"} textAlign={"left"}>
    {children}
  </Text>
);

const AdSetForm = forwardRef<
  ReturnType<typeof useForm<AdSetFormData>>,
  StepProps
>(
  (
    {
      adset,
      data,
      pixels = [],
      campaign,
      navigateTo,
      search,
      currency,
      loading: _outsideLoading,
      instagramAccounts,
      pages,
    },
    ref
  ) => {
    const [loading, setLoading] = useLoading({
      image: false,
      audience: false,
      targeting: false,
    });

    const [isEditingPixel, setIsEditingPixel] = useState(false);
    const [customConversions, setCustomConversions] = useState<
      EnrichedCustomConversion[]
    >([]);
    const [customConversionsLimit, setCustomConversionsLimit] = useState<
      number | undefined
    >(undefined);
    const [manageCustomConversionsOpen, setManageCustomConversionsOpen] =
      useState(false);

    // Use mock pixels if no pixels are provided
    const availablePixels = pixels || [];

    const [audienceColumns, setAudienceColumns] = useState<{
      sample: string[][];
      schema_fields: string[];
      file_key: string;
      file_name: string;
      file_description: string;
      saved_schema_fields: string[];
    }>({
      sample: [],
      schema_fields: [],
      file_key: "",
      file_name: "",
      file_description: "",
      saved_schema_fields: [],
    });

    const form = useForm<AdSetFormData>({
      resolver: yupResolver(AdSetFormSchema),
      mode: "onChange",
      criteriaMode: "all",
      reValidateMode: "onChange",

      values: adsetTransform.input({ adset, campaign, pixels }),
    });
    const { register, setValue, watch, control } = form;
    // Expose form methods to parent component through ref
    useImperativeHandle(ref, () => form);

    const { state: audiencesState, actions: audiencesActions } = useAudiences();

    const ad_account_id = watch("account_id");
    const refreshCustomConversions = useCallback(() => {
      if (!ad_account_id) {
        setCustomConversions([]);
        setCustomConversionsLimit(undefined);
        return Promise.resolve();
      }

      return actions.ADMIN.listCustomConversions({ ad_account_id })
        .then(
          (res: {
            data?: EnrichedCustomConversion[];
            meta?: { customConversionsLimit?: number };
          }) => {
            setCustomConversions(Array.isArray(res?.data) ? res.data : []);
            setCustomConversionsLimit(res?.meta?.customConversionsLimit);
          }
        )
        .catch(() => {
          setCustomConversions([]);
          setCustomConversionsLimit(undefined);
        });
    }, [ad_account_id]);

    useEffect(() => {
      if (ad_account_id) {
        audiencesActions.list({
          ad_account_id: ad_account_id,
          next: false,
        });
      }
    }, [audiencesActions.list, ad_account_id]);

    useEffect(() => {
      refreshCustomConversions();
    }, [refreshCustomConversions]);

    const customConversionsCount = customConversions.length;

    const isLoading =
      _outsideLoading.adsets ||
      _outsideLoading.campaigns ||
      _outsideLoading.audiences ||
      _outsideLoading.pixels ||
      _outsideLoading.publish ||
      _outsideLoading.pages ||
      _outsideLoading.instagramAccounts;

    const { open: openColumnMatchingDialog, close: closeColumnMatchingDialog } =
      useDialogWithOptions(ColumnMatchingModalContext);

    const isDateInThePast = (date: string | undefined) => {
      if (!date) return false;

      const timezone = watch("timezone");
      if (!timezone) return false;

      // Parse the start time in the specified timezone
      const startTimeInTimezone = dayjs.tz(date, timezone);
      const nowInTimezone = dayjs.tz(new Date(), timezone);

      return startTimeInTimezone.isBefore(nowInTimezone);
    };

    const isBudgetCompleted = Boolean(
      watch("budget.amount.amount") > 0 &&
        watch("start_time") &&
        watch("stop_time")
    );

    const isAudienceCompleted = Boolean(
      (watch("age_max") && watch("age_min") && watch("countries")) ||
        watch("audience_type") === "advantage"
    );

    const isConversionTrackingCompleted = Boolean(watch("pixel_id"));
    const isAdvancedSettingsCompleted = Boolean(isConversionTrackingCompleted);

    return (
      <FormProvider {...form}>
        <VStack w="full" textAlign={"left"} overflow="hidden">
          <ColumnMatchingModal
            title="Matching columns"
            audienceColumns={audienceColumns}
            onSave={async ({
              file_key,
              schema_fields,
              file_name,
              file_description,
            }) => {
              const custom_audiences_with_files = watch(
                `custom_audiences`
              ) as Array<AudienceData>;

              const index = custom_audiences_with_files.findIndex(
                (c) => "file_key" in c && c.file_key === file_key
              );

              if (index !== -1) {
                const editingAudience = custom_audiences_with_files[index];
                setValue(
                  `custom_audiences.${index}`,
                  {
                    ...editingAudience,
                    mapping: schema_fields,
                    schema_fields: schema_fields,
                    file_key: file_key,
                    name: file_name,
                    description: file_description,
                    // created_at: new Date().toISOString(),
                    audience_type: "csv",
                  } as CsvAudienceData,
                  {
                    shouldDirty: true,
                    shouldValidate: true,
                  }
                );
              } else {
                setValue(`custom_audiences`, [
                  ...watch(`custom_audiences`),
                  {
                    mapping: schema_fields,
                    file_key: file_key,
                    name: file_name,
                    description: file_description,
                    audience_type: "csv",
                    schema_fields: schema_fields,
                  } as CsvAudienceData,
                ]);
              }

              closeColumnMatchingDialog();
            }}
          />
          <VStack
            w="full"
            gap={[2, 4]}
            p={[2, 4]}
            px={[4, 8]}
            alignItems="flex-start"
          >
            <Text
              w="full"
              fontSize={["lg", "xl"]}
              fontWeight={"semibold"}
              textAlign={"left"}
            >
              {adset?.id ? `Edit ${adset.name}` : "New ad set"}
            </Text>

            <VStack w="full" spacing={1}>
              <InputLabel>Ad set name</InputLabel>
              <WhiteBgInput
                placeholder="New sales ad set"
                {...register(`name`, { required: true })}
              />
            </VStack>
            <Divider borderColor={"gray.200"} />

            <InfoSection
              title="Budget and Schedule"
              w="full"
              isCompleted={isBudgetCompleted}
              description="Set how much you’d like to spend and for how long this ad set should run"
              rightIcon={
                <Icon as={FaCalendar} fontSize={32} color="gray.400" />
              }
            />
            <Stack
              w="full"
              justifyContent="space-between"
              direction={["column", "row"]}
            >
              {campaign?.budget ? (
                <Container w="initial" flexGrow={1} gap={4}>
                  <Text fontWeight={"semibold"}>Campaign budget</Text>
                  <Text>
                    {campaign?.budget?.amount?.units}{" "}
                    {campaign?.budget?.amount?.currency}
                  </Text>
                  <Button
                    variant={"outline"}
                    size={"sm"}
                    onClick={() => {
                      navigateTo("edit_campaign");
                    }}
                  >
                    Edit
                  </Button>
                </Container>
              ) : (
                <BudgetSelector
                  value={watch("budget")}
                  onChange={(budget) => {
                    setValue("budget", budget, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                  currency={currency}
                  budgetTypeDisabled={adset?.status === "ACTIVE"}
                  disabled={isLoading}
                />
              )}
              <Box
                h="full"
                p={4}
                flexGrow={1}
                borderRadius="xl"
                border="1px solid"
                borderColor="gray.200"
                bg="white"
                shadow="md"
              >
                <VStack w="full" spacing={1}>
                  <Text fontWeight={"semibold"} w="full">
                    Schedule (start to end)
                  </Text>
                  <WhiteBgInput
                    type="datetime-local"
                    isDisabled={
                      isLoading ||
                      (adset?.status === "ACTIVE" &&
                        isDateInThePast(watch("start_time")))
                    }
                    py={6}
                    {...register(`start_time`, {
                      validate: (value) => {
                        if (isDateInThePast(value)) {
                          return "Start time must be in the future";
                        }
                        return true;
                      },
                    })}
                  />
                  <WhiteBgInput
                    type="datetime-local"
                    isDisabled={
                      isLoading ||
                      (adset?.status === "ACTIVE" &&
                        isDateInThePast(watch("stop_time")))
                    }
                    py={6}
                    {...register(`stop_time`)}
                  />
                </VStack>
              </Box>
            </Stack>

            <Divider borderColor={"gray.200"} />

            <InfoSection
              title="Audience"
              isCompleted={isAudienceCompleted}
              description="We’ll automatically reach people most likely to respond"
              rightIcon={<Icon as={FaUsers} fontSize={32} color="gray.400" />}
            />
            <AudienceTypeSelector
              instagramAccounts={instagramAccounts}
              pages={pages}
              initialType={watch("audience_type") as "advantage" | "manual"}
              search={search}
              loading={loading}
              onAudienceTypeChange={(type) => {
                setValue("audience_type", type);
              }}
            />

            <Divider borderColor={"gray.200"} />

            <CollapsibleContainer
              as={VStack}
              defaultCollapsed={isAdvancedSettingsCompleted}
              gap={4}
              w="full"
              head={
                <InfoSection
                  title="Advanced Settings"
                  isCompleted={isAdvancedSettingsCompleted}
                  description="These settings are already optimized for strong results. No changes are needed, unless you’d like to customize further."
                  w="full"
                />
              }
            >
              {!availablePixels.length ? (
                <WarningBox
                  title="No pixels found"
                  description="Make sure you have a pixel configured on your Ad Account and that you've connected that to the account."
                />
              ) : null}
              <Divider borderColor={"gray.200"} />
              <InfoSection
                title="Conversion Tracking"
                isCompleted={isConversionTrackingCompleted}
                description="These settings are already optimized for strong results. No changes are needed, unless you’d like to customize further."
                rightIcon={<Icon as={FaInfo} fontSize={32} color="gray.400" />}
              />
              <Stack
                w="full"
                justifyContent="space-between"
                direction={["column", "row"]}
              >
                <Box
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="gray.200"
                  p={4}
                  flexGrow={1}
                >
                  <HStack w="full" justifyContent="space-between">
                    <VStack alignItems={"flex-start"} spacing={0}>
                      <Text fontWeight={"semibold"}>Ticket Pixel</Text>
                      <Text fontSize={"sm"} color={"gray.500"}>
                        {watch("pixel_id")
                          ? availablePixels.find(
                              (p) => p.id === watch("pixel_id")
                            )?.name || "No pixel selected"
                          : null}
                      </Text>
                    </VStack>
                    {!isEditingPixel ? (
                      <Button
                        variant={"outline"}
                        size={"sm"}
                        onClick={() => setIsEditingPixel(true)}
                      >
                        Edit
                      </Button>
                    ) : (
                      <HStack spacing={2}>
                        <Controller
                          control={control}
                          name={`pixel_id`}
                          render={({ field }) => (
                            <Select
                              chakraStyles={{
                                container: () => ({
                                  position: "relative",
                                  width: "200px",
                                  borderRadius: "md",
                                }),
                              }}
                              size="sm"
                              // isClearable
                              value={
                                availablePixels.find(
                                  (p) => p.id === field.value
                                )
                                  ? {
                                      label:
                                        availablePixels.find(
                                          (p) => p.id === field.value
                                        )?.name || "",
                                      value: field.value,
                                    }
                                  : null
                              }
                              options={availablePixels.map((p) => ({
                                label: p.name,
                                value: p.id,
                              }))}
                              onChange={(option) =>
                                field.onChange(option?.value || null)
                              }
                            />
                          )}
                        />
                        <Button
                          variant={"outline"}
                          size={"sm"}
                          onClick={() => setIsEditingPixel(false)}
                        >
                          Done
                        </Button>
                      </HStack>
                    )}
                  </HStack>
                </Box>
                <Box
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="gray.200"
                  p={4}
                  flexGrow={1}
                >
                  <HStack w="full" justifyContent="space-between">
                    <VStack alignItems={"flex-start"} spacing={0}>
                      <Text fontWeight={"semibold"}>
                        Custom conversion event
                      </Text>
                      <Text fontSize={"sm"} color={"gray.500"}>
                        Tracks purchases for this campaign
                      </Text>
                    </VStack>
                    <KYDSwitch
                      defaultChecked={watch("custom_conversion_event")}
                      isChecked={watch("custom_conversion_event")}
                      onChange={(value) => {
                        setValue(
                          "custom_conversion_event",
                          value.target.checked
                        );
                      }}
                    />
                  </HStack>
                </Box>
              </Stack>
              <CustomConversionsWarning
                count={customConversionsCount}
                limit={customConversionsLimit}
                threshold={5}
                onManageClick={() => setManageCustomConversionsOpen(true)}
                showManageLinkBelowThreshold={true}
              />
              {ad_account_id ? (
                <ManageCustomConversionsModal
                  isOpen={manageCustomConversionsOpen}
                  onClose={() => setManageCustomConversionsOpen(false)}
                  conversions={customConversions}
                  ad_account_id={ad_account_id}
                  customConversionsLimit={customConversionsLimit}
                  onDeleted={refreshCustomConversions}
                />
              ) : null}
            </CollapsibleContainer>
          </VStack>
        </VStack>
      </FormProvider>
    );
  }
);

export default withColumnMatchingModal(AdSetForm);
